import { useEffect, useRef, useState } from 'react';
import { CHROME_EXTENSION_URL } from '../../lib/constants';
import { capture } from '@/lib/analytics';
import { ChromeIcon } from '../shared/ChromeIcon';
import { ResetDemoModal } from './ResetDemoModal';

export interface DemoBannerProps {
  projectId: string;
}

/**
 * Copy and actions share a row on wide screens. On phones the actions wrap
 * below the explanation while dismissal stays in the top-right corner.
 *
 * Both install events carry `source: 'demo_banner'`, reusing the pair the other
 * four install offers already share rather than minting new names, so the
 * boundary is a step-up splittable by `source`. Unlike `global_banner` and
 * `issues_empty_state`, this surface is NOT gated on extension detection (it
 * also carries Reset demo), so its `shown` population includes people who
 * already have the extension. Split by `source` before pooling.
 * The sample label and explanation each stay together, wrapping as separate
 * pieces on narrow screens. The quiet treatment matches the project prototype.
 */
export function DemoBanner({ projectId }: DemoBannerProps) {
  const [dismissed, setDismissed] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  // Fire-once: StrictMode double-invokes this effect on the same instance in
  // dev, and a ref survives that synthetic unmount, so the offer is reported
  // exactly once in both dev and production (pinned by a StrictMode control in
  // DemoBanner.test.tsx).
  const shownRef = useRef(false);
  useEffect(() => {
    if (shownRef.current) return;
    shownRef.current = true;
    capture('extension_install_banner_shown', { source: 'demo_banner' });
  }, []);

  if (dismissed) return null;
  return (
    <>
      <section
        className="pb-4"
        role="region"
        aria-label="Demo project banner"
      >
        <div className="relative flex flex-col md:flex-row md:items-center gap-3 pr-8">
          <p className="flex-1 min-w-0 text-xs text-gray-500">
            <strong className="inline-block align-middle mr-3 rounded border border-gray-200 bg-primary-50 px-2 py-1 font-medium text-primary-600">Sample project</strong>{' '}
            <span className="inline-block align-middle">Explore a complete QA workflow.</span>
          </p>
          <div className="flex flex-wrap md:flex-shrink-0 gap-2">
            <button
              type="button"
              className="btn-ghost btn-sm gap-2 text-primary-600 hover:text-primary-700"
              onClick={() => {
                capture('dashboard_extension_install_click', { source: 'demo_banner' });
                window.open(CHROME_EXTENSION_URL, '_blank', 'noopener');
              }}
            >
              <ChromeIcon className="w-4 h-4 flex-shrink-0" />
              <span className="text-cap-trim">Install Chrome extension</span>
            </button>
            <button type="button" className="btn-secondary btn-sm" onClick={() => setResetOpen(true)}>
              <span className="text-cap-trim">Reset demo</span>
            </button>
          </div>
          <button
            type="button"
            className="btn-ghost btn-sm absolute top-0 right-0 w-7 p-0 text-primary-600 hover:text-primary-700"
            onClick={() => setDismissed(true)}
            aria-label="Dismiss"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </section>
      <ResetDemoModal projectId={projectId} isOpen={resetOpen} onClose={() => setResetOpen(false)} />
    </>
  );
}
